import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useAuth } from '../../context/AuthContext';
import {
  getUserInfo,
  updateCompanyInfo,
  updateUserContactInfo,
  changeUserPassword,
  updateEmailAlert,
} from '../../services/accountService';
import {
  FaUser,
  FaBuilding,
  FaLock,
  FaBell,
  FaCreditCard,
  FaChartBar,
  FaPlus,
  FaEye,
  FaFileContract,
  FaRocket,
} from 'react-icons/fa';
import CompanyDetails from './CompanyDetails';
import UserDetails from './UserDetails';
import ChangePassword from './ChangePassword';
import EmailAlerts from './EmailAlerts';
import MySubscription from './MySubscription';
import PaymentHistory from './PaymentHistory';
import DailyAlerts from './DailyAlerts';

const SubscriptionRequiredNotice = ({ feature }) => {
  const nav = useNavigate();
  return (
    <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl border border-indigo-200 p-8 text-center">
      <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
        <FaBell className="text-indigo-400 text-2xl" />
      </div>
      <h3 className="text-xl font-bold text-gray-800 mb-2">{feature}</h3>
      <p className="text-gray-500 text-sm mb-4 max-w-md mx-auto">
        Functionalitatea <strong>{feature}</strong> este disponibila exclusiv pentru utilizatorii cu abonament activ.
        Activeaza un plan pentru a primi notificari personalizate despre licitatii relevante.
      </p>
      <button
        onClick={() => nav('/abonamente')}
        className="px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition shadow-md"
      >
        Vezi planurile de abonament
      </button>
    </div>
  );
};

const UserAccount = ({ setIsAuthenticated }) => {
  const { tab: urlTab } = useParams();
  const navigate = useNavigate();
  const { hasActiveSubscription } = useAuth();
  const [userData, setUserData] = useState({
    emailAlerts: false,
    alertTypes: [],
  });
  const [activeTab, setActiveTab] = useState(urlTab || 'dashboard');
  const [companyEdit, setCompanyEdit] = useState(false);
  const [contactEdit, setContactEdit] = useState(false);
  const [passwordEdit, setPasswordEdit] = useState(false);
  const [emailAlertsEdit, setEmailAlertsEdit] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Add mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      const { success, error, user } = await getUserInfo();
      if (success) {
        setUserData({
          ...user,
          emailAlerts: user.userAlerts?.emailAlertsEnabled ?? false,
          alertTypes: user.userAlerts?.alertTypes ?? [],
        });
      } else {
        setError(
          error || 'A apărut o eroare la încărcarea datelor utilizatorului.',
        );
      }
    };

    fetchUserData();
  }, []);

  // Sync URL tab parameter with active tab
  useEffect(() => {
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [urlTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/account/${tabId}`, { replace: true });
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (window.prerenderReady !== undefined) {
      window.prerenderReady = true;
    }
  }, []);

  const handleCompanySave = async () => {
    const success = await updateCompanyInfo(userData);
    if (success) {
      setCompanyEdit(false);
      setSuccess('Informațiile despre companie au fost actualizate cu succes.');
    }
  };

  const handleContactSave = async () => {
    const success = await updateUserContactInfo(userData);
    if (success) {
      setContactEdit(false);
      setSuccess('Informațiile de contact au fost actualizate cu succes.');
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const currentPassword = formData.get('currentPassword');
    const newPassword = formData.get('newPassword');
    const confirmNewPassword = formData.get('confirmNewPassword');

    if (newPassword !== confirmNewPassword) {
      setError('Parolele nu se potrivesc.');
      return;
    }

    try {
      const success = await changeUserPassword({
        currentPassword,
        newPassword,
      });

      if (success) {
        setPasswordEdit(false);
        setSuccess('Parola a fost schimbată cu succes.');
        setError('');
      } else {
        setError(
          'A apărut o eroare la schimbarea parolei. Verificați parola curentă.',
        );
      }
    } catch (err) {
      setError(err.message || 'O eroare neașteptată a apărut.');
    }
  };

  const handleEmailAlertsSave = async () => {
    const updateEmailAlertResponse = await updateEmailAlert(
      userData.emailAlerts,
      userData.alertTypes || [], // Asigură-te că trimite o listă, chiar dacă este goală
    );

    if (updateEmailAlertResponse.success) {
      setEmailAlertsEdit(false);
      setSuccess(
        'Preferințele pentru alertele pe email au fost actualizate cu succes.',
      );
    } else {
      // Afisează exact mesajul de eroare primit de la server
      setError(
        updateEmailAlertResponse.message ||
          'A apărut o eroare la actualizarea preferințelor pentru alertele pe email.',
      );
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { label: 'Cont Utilizator' },
  ];

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: FaChartBar },
    { id: 'profile', label: 'Profil Personal', icon: FaUser },
    { id: 'company', label: 'Detalii Companie', icon: FaBuilding },
    { id: 'password', label: 'Schimbă Parola', icon: FaLock },
    { id: 'emailAlerts', label: 'Alerte Email', icon: FaBell },
    { id: 'dailyAlerts', label: 'Alerte Zilnice', icon: FaPlus },
    { id: 'subscription', label: 'Abonament', icon: FaCreditCard },
    { id: 'payments', label: 'Istoric Plati', icon: FaFileContract },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <FaChartBar className="text-indigo-500" />
                Dashboard Licitatii-Constructii
              </h2>

              {/* Acțiuni rapide */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-6 text-white mb-8">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                  <FaRocket />
                  Acțiuni Rapide
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <button
                    type="button"
                    onClick={() => handleTabChange('dailyAlerts')}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 transition-all p-3 sm:p-4 rounded-lg text-center"
                  >
                    <FaPlus className="text-xl sm:text-2xl mb-2 mx-auto" />
                    <div className="text-xs sm:text-sm font-semibold">
                      Adaugă Alertă
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/licitatii/')}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 transition-all p-3 sm:p-4 rounded-lg text-center"
                  >
                    <FaEye className="text-xl sm:text-2xl mb-2 mx-auto" />
                    <div className="text-xs sm:text-sm font-semibold">
                      Vezi Licitații
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/proceduri-salvate')}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 transition-all p-3 sm:p-4 rounded-lg text-center"
                  >
                    <FaFileContract className="text-xl sm:text-2xl mb-2 mx-auto" />
                    <div className="text-xs sm:text-sm font-semibold">
                      Rapoarte
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTabChange('subscription')}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 transition-all p-3 sm:p-4 rounded-lg text-center"
                  >
                    <FaCreditCard className="text-xl sm:text-2xl mb-2 mx-auto" />
                    <div className="text-xs sm:text-sm font-semibold">
                      Abonament
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'profile':
        return (
          <UserDetails
            userData={userData}
            setUserData={setUserData}
            contactEdit={contactEdit}
            setContactEdit={setContactEdit}
            handleContactSave={handleContactSave}
          />
        );
      case 'company':
        return (
          <CompanyDetails
            userData={userData}
            setUserData={setUserData}
            companyEdit={companyEdit}
            setCompanyEdit={setCompanyEdit}
            handleCompanySave={handleCompanySave}
          />
        );
      case 'password':
        return (
          <ChangePassword
            passwordEdit={passwordEdit}
            setPasswordEdit={setPasswordEdit}
            handlePasswordSave={handlePasswordSave}
          />
        );
      case 'emailAlerts':
        return hasActiveSubscription ? (
          <EmailAlerts
            userData={userData}
            setUserData={setUserData}
            emailAlertsEdit={emailAlertsEdit}
            setEmailAlertsEdit={setEmailAlertsEdit}
            handleEmailAlertsSave={handleEmailAlertsSave}
          />
        ) : (
          <SubscriptionRequiredNotice feature="Alerte Email" />
        );
      case 'dailyAlerts':
        return hasActiveSubscription ? (
          <DailyAlerts userData={userData} setUserData={setUserData} />
        ) : (
          <SubscriptionRequiredNotice feature="Alerte Zilnice" />
        );
      case 'subscription':
        return <MySubscription setIsAuthenticated={setIsAuthenticated} />;
      case 'payments':
        return <PaymentHistory />;
      default:
        return null;
    }
  };

  return (
    <>
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
      <section className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-green-100 py-8 sm:py-12">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {success && (
            <div className="fixed top-4 sm:top-6 left-1/2 transform -translate-x-1/2 bg-green-100 border border-green-400 text-green-700 px-4 sm:px-6 py-3 rounded-xl shadow-lg z-50 animate-fade-in max-w-xs sm:max-w-none text-sm sm:text-base text-center">
              {success}
            </div>
          )}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 sm:px-6 py-3 rounded-xl mb-6 animate-fade-in text-sm sm:text-base">
              {error}
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
            {/* Mobile Menu Button */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="w-full bg-white rounded-xl shadow-lg p-4 flex items-center justify-between"
              >
                <span className="text-lg font-semibold text-gray-800">
                  Meniu Cont (
                  {menuItems.find((item) => item.id === activeTab)?.label})
                </span>
                <span className="text-gray-500">
                  {isMobileMenuOpen ? '▲' : '▼'}
                </span>
              </button>
            </div>

            {/* Meniu stânga */}
            <div
              className={`w-full lg:w-1/4 bg-white rounded-2xl shadow-lg p-6 h-fit lg:sticky lg:top-8 ${
                isMobileMenuOpen ? 'block' : 'hidden lg:block'
              }`}
            >
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Meniul Contului
                </h3>
              </div>

              <nav className="space-y-2">
                {menuItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleTabChange(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-all duration-200 ${
                        activeTab === item.id
                          ? 'bg-indigo-500 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <IconComponent className="text-lg flex-shrink-0" />
                      <span className="font-medium text-sm sm:text-base truncate">
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Conținut dreapta */}
            <div className="flex-1 bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
              {renderContent()}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default UserAccount;
